import { useEffect, useState } from 'react';

import {
  readiness as fetchReadiness, templateUsage, globalSectionUsage,
  type OwnerRef, type PublishReadiness, type SectionUsage, type TemplateUsage,
} from '../../lib/cmsAdmin';
import { ApiError } from '../../lib/apiClient';

/**
 * Publish, with the consequences stated first.
 *
 * ── A TEMPLATE IS A LIVE REFERENCE ───────────────────────────────────────────────────
 *
 * Publishing a page changes one page. Publishing a template changes every event resolving
 * through it, at once, with no per-event write and no second confirmation. Somebody who
 * does not know that will eventually find out by doing it, so the count is fetched and
 * shown before the button, not after.
 *
 * ── READINESS IS ASKED, NOT GUESSED ──────────────────────────────────────────────────
 *
 * The same server code that will refuse the publish answers whether it would. The editor
 * has its own continuous validation, which is a courtesy for the admin; this is the
 * actual gate, asked in advance so its answer arrives while there is still something to
 * fix.
 */
export function PublishDialog({
  owner, name, onPublish, onClose, busy,
}: {
  owner: OwnerRef;
  name: string;
  onPublish: () => Promise<void>;
  onClose: () => void;
  busy: boolean;
}) {
  const [readiness, setReadiness] = useState<PublishReadiness | null>(null);
  const [usage, setUsage] = useState<TemplateUsage | null>(null);
  const [sectionUsage, setSectionUsage] = useState<SectionUsage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchReadiness(owner)
      .then((r) => { if (!cancelled) setReadiness(r); })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not check this draft.');
        }
      });
    if (owner.kind === 'template') {
      templateUsage(owner.id)
        .then((u) => { if (!cancelled) setUsage(u); })
        .catch(() => { if (!cancelled) setUsage(null); });
    }
    if (owner.kind === 'global_section') {
      globalSectionUsage(owner.id)
        .then((u) => { if (!cancelled) setSectionUsage(u); })
        .catch(() => { if (!cancelled) setSectionUsage(null); });
    }
    return () => { cancelled = true; };
  }, [owner]);

  const ready = readiness?.ready === true;

  return (
    <div className="cms-modal" role="dialog" aria-modal="true" aria-label="Publish">
      <div className="cms-modal__panel">
        <header className="cms-modal__head">
          <h2>Publish &ldquo;{name}&rdquo;</h2>
        </header>

        {error && <p className="alert alert--error" role="alert">{error}</p>}
        {!readiness && !error && <p className="adm-muted">Checking this draft…</p>}

        {readiness && !readiness.ready && (
          <div className="alert alert--error" role="alert">
            <strong>This draft cannot be published yet.</strong>
            <p>{readiness.message ?? describeReason(readiness.reason)}</p>
            {readiness.nodeId && <p className="adm-muted">Block: <code>{readiness.nodeId}</code></p>}
          </div>
        )}

        {readiness?.ready && (
          <p className="alert alert--success">
            This draft passes every publish check.
          </p>
        )}

        {owner.kind === 'template' && usage && <Impact usage={usage} />}

        {owner.kind === 'global_section' && <SectionImpact usage={sectionUsage} />}

        {owner.kind === 'page' && (
          <p className="cms-impact">
            This is a page override, so publishing changes <strong>this page only</strong>.
            No other event is affected.
          </p>
        )}

        <p className="adm-hint">
          The published version becomes live immediately. The version you are replacing is
          kept and can be brought back as a new draft.
        </p>

        <footer className="cms-modal__foot">
          <button type="button" className="button button--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="button button--primary" disabled={!ready || busy}
            onClick={() => { void onPublish(); }}>
            {busy ? 'Publishing…' : 'Publish'}
          </button>
        </footer>
      </div>
    </div>
  );
}

/** What publishing this template would change, in events rather than in rows. */
function Impact({ usage }: { usage: TemplateUsage }) {
  const total = usage.assigned + usage.viaDefault;
  return (
    <div className="cms-impact">
      <h3>What this changes</h3>
      {total === 0 && (
        <p>
          No live event resolves through this template yet, so publishing changes nothing
          a visitor can see today.
        </p>
      )}
      {total > 0 && (
        <ul>
          {usage.assigned > 0 && (
            <li>
              <strong>{usage.assigned}</strong> {usage.assigned === 1 ? 'event is' : 'events are'}
              {' '}assigned to this template directly.
            </li>
          )}
          {usage.viaDefault > 0 && (
            <li>
              Up to <strong>{usage.viaDefault}</strong> further
              {' '}{usage.viaDefault === 1 ? 'event' : 'events'} may also resolve through it,
              because it is the {usage.scope} default and they have no template of their own.
            </li>
          )}
        </ul>
      )}
      <p className="adm-muted">
        Counted across {usage.visibleEvents} live {usage.visibleEvents === 1 ? 'event' : 'events'},
        excluding test accounts.
      </p>
    </div>
  );
}

/**
 * §60. What publishing this global section changes.
 *
 * A section is the widest-reaching thing in the CMS: one footer can be on every page of
 * every organization that references it. "Footer is referenced by 4 templates and 2
 * pages" is the sentence that stops somebody treating it like editing one page.
 */
function SectionImpact({ usage }: { usage: SectionUsage | null }) {
  if (!usage) return <p className="cms-impact adm-muted">Counting what this affects…</p>;

  const referenced = usage.templates + usage.pages;
  return (
    <div className="cms-impact">
      <h3>What this changes</h3>
      {referenced === 0 && (
        <p>
          Nothing published references this section yet, so publishing changes nothing a
          visitor can see.
          {usage.drafts > 0 && (
            <> <strong>{usage.drafts}</strong> unpublished draft
              {usage.drafts === 1 ? '' : 's'} already reference
              {usage.drafts === 1 ? 's' : ''} it.</>
          )}
        </p>
      )}
      {referenced > 0 && (
        <ul>
          {usage.templates > 0 && (
            <li>
              <strong>{usage.templates}</strong> published template
              {usage.templates === 1 ? '' : 's'} reference{usage.templates === 1 ? 's' : ''} it.
            </li>
          )}
          {usage.pages > 0 && (
            <li>
              <strong>{usage.pages}</strong> published page{usage.pages === 1 ? '' : 's'}{' '}
              reference{usage.pages === 1 ? 's' : ''} it.
            </li>
          )}
          <li>
            <strong>{usage.events}</strong> live event{usage.events === 1 ? '' : 's'}{' '}
            currently show{usage.events === 1 ? 's' : ''} it.
          </li>
        </ul>
      )}
      <p className="adm-muted">
        Counted across {usage.visibleEvents} live events, excluding test accounts.
      </p>
    </div>
  );
}

function describeReason(reason: string): string {
  if (reason === 'no_draft') return 'There is no draft to publish. Make a change first.';
  return `The server refused this draft: ${reason}.`;
}
